import { Sparkline } from '@mantine/charts';
import { Group, Stack, Text } from '@mantine/core';
import { IconArrowDownRight, IconArrowUpRight, IconMinus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { TravelDelta, formatTravelNumber } from '../TravelAnalyticsUtils';

interface TravelStatTileProps {
  label: string;
  value: string;
  delta: TravelDelta | null;
  deltaCaption: string;
  trend: number[];
}

const SIGNS = ['-', '', '+'] as const;

function formatDelta({ absolute, percent }: TravelDelta) {
  const sign = SIGNS[Math.sign(absolute) + 1];
  const amount = `${sign}${formatTravelNumber(Math.abs(absolute))}`;
  return percent == null ? amount : `${amount} (${sign}${formatTravelNumber(Math.abs(Math.round(percent)))}%)`;
}

function DeltaIcon({ absolute }: TravelDelta) {
  if (absolute > 0) {
    return <IconArrowUpRight size={14} />;
  }
  if (absolute < 0) {
    return <IconArrowDownRight size={14} />;
  }
  return <IconMinus size={14} />;
}

export function TravelStatTile({ label, value, delta, deltaCaption, trend }: TravelStatTileProps) {
  const { t } = useTranslation();

  return (
    <Stack gap={4}>
      <Text
        size="xs"
        c="dimmed"
      >
        {label}
      </Text>
      <Text
        size="xl"
        fw={600}
      >
        {value}
      </Text>
      <Group
        gap={4}
        wrap="nowrap"
        c="dimmed"
      >
        {delta && <DeltaIcon {...delta} />}
        <Text size="xs">
          {delta ? `${formatDelta(delta)} ${deltaCaption}` : t('travel.registry.aggregates.yearly.noPrevious')}
        </Text>
      </Group>
      <Sparkline
        h={28}
        data={trend}
        color="primary.6"
        curveType="linear"
        fillOpacity={0.1}
        strokeWidth={2}
      />
    </Stack>
  );
}
